import { useMemo } from "react";
import {
  Boxes,
  BoxSelect,
  Crosshair,
  Eye,
  EyeOff,
  Focus,
  Gauge,
  Home,
  Loader2,
  Tag,
  Layers,
  MousePointer2,
  Move,
  Palette,
  Ruler,
  Scissors,
  Search,
  Table2,
  Trees,
} from "lucide-react";
import { DEPARTMENTS } from "@/constants/workflow";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Slider } from "@/components/ui/slider";

function Tool({ icon: Icon, label, active, onClick, testId, pulseWhenActive = false }) {
  return (
    <button
      data-testid={testId}
      title={label}
      onClick={onClick}
      className={`okn-tool grid shrink-0 place-items-center rounded-full text-slate-700 hover:bg-slate-100 h-10 w-10 sm:h-9 sm:w-9 ${
        active ? "okn-tool-active" : ""
      } ${active && pulseWhenActive ? "okn-tool-pulse" : ""}`}
    >
      <Icon size={16} />
    </button>
  );
}

export function ViewerToolbar({
  mode,
  setMode,
  onFit,
  onHome,
  onIsolate,
  onHide,
  onShowAll,
  colorMode,
  setColorMode,
  section,
  setSection,
  toggleTree,
  toggleTable,
  toggleSearch,
  statsOpen,
  toggleStats,
  labelsOn,
  toggleLabels,
  treeOpen,
  tableOpen,
  selectionCount,
  shifted = false,
}) {
  const colorOptions = [{ key: "original", label: "Original" }, ...DEPARTMENTS];
  if (colorMode === "compare") colorOptions.push({ key: "compare", label: "Revision Diff" });
  const activeColor = colorOptions.find((o) => o.key === colorMode);
  const sectionValue = useMemo(() => [section.value * 100], [section.value]);

  return (
    <>
      <div className="okn-scroll pointer-events-auto absolute left-1/2 top-3 z-20 flex max-w-[calc(100vw-1rem)] -translate-x-1/2 items-center gap-1 overflow-x-auto rounded-full border border-slate-300 bg-white/95 p-1.5 shadow-2xl backdrop-blur-md sm:top-4 sm:max-w-[calc(100vw-1.5rem)]">
        <Tool
          icon={MousePointer2}
          label="Select (Shift-click for multi-select)"
          active={mode === "select"}
          onClick={() => setMode("select")}
          testId="tool-select"
        />
        <Tool
          icon={BoxSelect}
          label="Box select — drag a rectangle to select many panels"
          active={mode === "box"}
          onClick={() => setMode(mode === "box" ? "select" : "box")}
          pulseWhenActive
          testId="tool-box-select"
        />
        <Tool
          icon={Move}
          label="Pan"
          active={mode === "pan"}
          onClick={() => setMode("pan")}
          testId="tool-pan"
        />
        <Tool icon={Focus} label="Fit to selection" onClick={onFit} testId="tool-fit" />
        <Tool icon={Home} label="Home view" onClick={onHome} testId="tool-home" />
        <span className="mx-1 h-6 w-px bg-white/10" />
        <Tool icon={Crosshair} label="Isolate selection" onClick={onIsolate} testId="tool-isolate" />
        <Tool icon={EyeOff} label="Hide selection" onClick={onHide} testId="tool-hide" />
        <Tool icon={Eye} label="Show all" onClick={onShowAll} testId="tool-show-all" />
        <span className="mx-1 h-6 w-px bg-white/10" />
        <Tool
          icon={Ruler}
          label="Measure distance"
          active={mode === "measure"}
          onClick={() => setMode(mode === "measure" ? "select" : "measure")}
          pulseWhenActive
          testId="tool-measure"
        />
        <Tool
          icon={Scissors}
          label="Section plane"
          active={!!section.axis}
          onClick={() => setSection(section.axis ? { axis: null, value: 1 } : { axis: "z", value: 1 })}
          pulseWhenActive
          testId="tool-section"
        />
        <span className="mx-1 h-6 w-px bg-white/10" />
        <Tool icon={Trees} label="Model & layer tree" active={treeOpen} onClick={toggleTree} testId="tool-tree" />
        <Tool icon={Search} label="Search panels" onClick={toggleSearch} testId="tool-search" />
        <Tool icon={Table2} label="Panel list tab" onClick={toggleTable} testId="tool-table" />
        <Tool
          icon={Tag}
          label="Block definition labels"
          active={labelsOn}
          onClick={toggleLabels}
          testId="tool-labels"
        />
        <Tool
          icon={Gauge}
          label="Performance panel"
          active={statsOpen}
          onClick={toggleStats}
          testId="tool-stats"
        />
      </div>

      {selectionCount > 0 ? (
        <div
          data-testid="selection-badge"
          className="mono pointer-events-none absolute left-1/2 z-20 -translate-x-1/2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-4 py-1.5 text-center text-[11px] uppercase tracking-wider text-cyan-700 backdrop-blur-md top-[11rem] lg:top-[4.75rem]"
        >
          {selectionCount} selected
        </div>
      ) : null}

      <div
        className={`pointer-events-auto absolute top-16 z-20 flex flex-col gap-2 lg:top-4 ${
          shifted ? "left-3 lg:left-[19.5rem]" : "left-3 lg:left-4"
        }`}
      >
        <div className="flex flex-wrap items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="color-by-trigger"
              className={`okn-tool flex items-center gap-2 rounded-full border border-slate-300 bg-white/95 px-4 py-2 text-xs text-slate-800 shadow-xl backdrop-blur-md ${
                colorMode !== "original" ? "okn-tool-pulse" : ""
              }`}
            >
              <Palette size={15} className="text-cyan-600" />
              <span className="mono uppercase tracking-wider">
                Color: {activeColor?.label || "Original"}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="border-slate-200 bg-white text-slate-800">
            {colorOptions.map((option) => (
              <DropdownMenuItem
                key={option.key}
                data-testid={`color-by-${option.key}`}
                onClick={() => setColorMode(option.key)}
                className="mono text-xs uppercase tracking-wider focus:bg-cyan-500/15 focus:text-cyan-700"
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        </div>

        {section.axis ? (
          <div className="w-52 rounded-xl border border-slate-300 bg-white/95 p-3 backdrop-blur-md">
            <div className="flex items-center justify-between">
              <span className="mono text-[10px] uppercase tracking-wider text-slate-600">
                Section
              </span>
              <div className="flex gap-1">
                {["x", "y", "z"].map((axis) => (
                  <button
                    key={axis}
                    data-testid={`section-axis-${axis}`}
                    onClick={() => setSection({ ...section, axis })}
                    className={`mono h-6 w-6 rounded text-[11px] uppercase ${
                      section.axis === axis
                        ? "bg-cyan-500/20 text-cyan-700"
                        : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {axis}
                  </button>
                ))}
              </div>
            </div>
            <Slider
              data-testid="section-slider"
              value={sectionValue}
              onValueChange={([v]) => setSection({ ...section, value: v / 100 })}
              max={100}
              step={1}
              className="mt-4"
            />
          </div>
        ) : null}
      </div>
    </>
  );
}

/**
 * Dedicated options row rendered directly below the filter bar (in normal layout
 * flow, so it can never overlap the canvas overlays): Labels, Detailed model and
 * Detail selected controls.
 */
export function ViewerOptionsBar({
  labelsOn,
  labelTargets = { panels: true, anchors: true },
  onLabelTarget,
  detailMode = "full",
  detailProgress = null,
  detailAvailable = false,
  onToggleDetail,
  onDetailSelection,
  onClearDetail,
  selectionCount,
}) {
  return (
    <div
      data-testid="model-options-bar"
      className="okn-scroll flex shrink-0 items-center gap-2 overflow-x-auto border-b border-slate-200 bg-white px-3 py-1.5 lg:flex-wrap lg:overflow-visible"
    >
      <div
        data-testid="labels-group"
        className="mono flex items-center gap-3 rounded-2xl border border-slate-300 bg-white/95 px-3.5 py-1.5 text-[11px] uppercase tracking-wider text-slate-700"
      >
        <span className="flex items-center gap-2">
          <Tag size={13} className={labelsOn ? "text-cyan-600" : "text-slate-400"} />
          Labels
        </span>
        <span className="flex items-center gap-3 border-l border-slate-200 pl-3">
          {[
            ["panels", "Panels"],
            ["anchors", "Anchors"],
          ].map(([key, label]) => (
            <span key={key} className="flex items-center justify-between gap-3">
              <span className="text-[10px] text-slate-600">{label}</span>
              <button
                data-testid={`labels-toggle-${key}`}
                onClick={() => onLabelTarget?.(key)}
                className={`relative h-4 w-8 rounded-full transition-colors ${
                  labelTargets[key] ? "bg-cyan-500" : "bg-slate-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${
                    labelTargets[key] ? "left-4" : "left-0.5"
                  }`}
                />
              </button>
            </span>
          ))}
        </span>
      </div>

      {detailAvailable ? (
        <div className="mono flex items-center gap-2 rounded-full border border-slate-300 bg-white/95 px-3.5 py-2 text-[11px] uppercase tracking-wider text-slate-700">
          <Boxes
            size={13}
            className={detailMode !== "light" ? "text-cyan-600" : "text-slate-400"}
          />
          Detailed model
          <button
            data-testid="detail-toggle-chip"
            disabled={!!detailProgress}
            onClick={onToggleDetail}
            title="Load and show the real detailed geometry"
            className={`relative h-4 w-8 rounded-full transition-colors disabled:opacity-50 ${
              detailMode === "full" ? "bg-cyan-500" : "bg-slate-300"
            }`}
          >
            <span
              className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${
                detailMode === "full" ? "left-4" : "left-0.5"
              }`}
            />
          </button>
        </div>
      ) : null}

      {detailAvailable && (selectionCount > 0 || detailProgress || detailMode === "selected") ? (
        <>
          {selectionCount > 0 ? (
            <button
              data-testid="detail-selection-button"
              disabled={!!detailProgress}
              onClick={onDetailSelection}
              className="mono flex items-center gap-1.5 rounded-full border border-cyan-500/40 bg-white/95 px-3.5 py-2 text-[11px] uppercase tracking-wider text-cyan-700 hover:bg-cyan-50 disabled:opacity-50"
            >
              <Boxes size={13} /> Detail selected ({selectionCount})
            </button>
          ) : null}

          {detailMode === "selected" ? (
            <button
              data-testid="detail-clear-button"
              onClick={onClearDetail}
              title="Return detailed panels to their boxes"
              className="mono flex items-center gap-1.5 rounded-full border border-slate-300 bg-white/95 px-3.5 py-2 text-[11px] uppercase tracking-wider text-slate-600 transition-colors hover:border-cyan-500/60 hover:text-cyan-700"
            >
              Clear
            </button>
          ) : null}

          {detailProgress ? (
            <span
              data-testid="detail-progress"
              className="mono flex items-center gap-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-[10.5px] uppercase tracking-wider text-cyan-700"
            >
              <Loader2 size={12} className="animate-spin" />
              Loading detail {detailProgress.loaded}/{detailProgress.total}
            </span>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

export function LayerLegend({ layers, hidden, toggle, disabled = false }) {
  return (
    <div className={`border-t border-slate-200 p-3 ${disabled ? "opacity-50" : ""}`}>
      <p className="mono mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
        <Layers size={12} /> Layers
      </p>
      {disabled ? (
        <p data-testid="layers-light-hint" className="mb-2 text-[10.5px] text-slate-500">
          Available in the detailed model view.
        </p>
      ) : null}
      <div className="space-y-1">
        {layers.map((layer) => (
          <button
            key={layer.path}
            disabled={disabled}
            data-testid={`layer-toggle-${layer.path}`}
            onClick={() => toggle(layer.path)}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-slate-100 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-sm"
              style={{ backgroundColor: layer.color }}
            />
            <span className={hidden.has(layer.path) ? "text-slate-500 line-through" : "text-slate-700"}>
              {layer.path}
            </span>
            {hidden.has(layer.path) ? (
              <EyeOff size={11} className="ml-auto text-slate-500" />
            ) : (
              <Eye size={11} className="ml-auto text-slate-500" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
